/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */

import type { Matrix3 } from '../matrix/Matrix3';

export class Vector2 {
  public x: number;
  public y: number;

  constructor(x?: number, y?: number) {
    this.x = x || 0;
    this.y = y || 0;
  }

  public get width(): number {
    return this.x;
  }

  public set width(value: number) {
    this.x = value;
  }

  public get height(): number {
    return this.y;
  }

  public set height(value: number) {
    this.y = value;
  }

  public set(x: number, y: number): this {
    this.x = x;
    this.y = y;

    return this;
  }

  public setScalar(scalar: number): this {
    this.x = scalar;
    this.y = scalar;

    return this;
  }

  public setX(x: number): this {
    this.x = x;

    return this;
  }

  public setY(y: number): this {
    this.y = y;

    return this;
  }

  public clone(): Vector2 {
    return new Vector2(this.x, this.y);
  }

  public copy(v: Vector2): this {
    this.x = v.x;
    this.y = v.y;

    return this;
  }

  public add(v: Vector2): this {
    this.x += v.x;
    this.y += v.y;

    return this;
  }

  public addScalar(s: number): this {
    this.x += s;
    this.y += s;

    return this;
  }

  public addVectors(a: Vector2, b: Vector2): this {
    this.x = a.x + b.x;
    this.y = a.y + b.y;

    return this;
  }

  public addScaledVector(v: Vector2, s: number): this {
    this.x += v.x * s;
    this.y += v.y * s;

    return this;
  }

  public sub(v: Vector2): this {
    this.x -= v.x;
    this.y -= v.y;

    return this;
  }

  public subScalar(s: number): this {
    this.x -= s;
    this.y -= s;

    return this;
  }

  public subVectors(a: Vector2, b: Vector2): this {
    this.x = a.x - b.x;
    this.y = a.y - b.y;

    return this;
  }

  public multiply(v: Vector2): this {
    this.x *= v.x;
    this.y *= v.y;

    return this;
  }

  public multiplyScalar(scalar: number): this {
    this.x *= scalar;
    this.y *= scalar;

    return this;
  }

  public divide(v: Vector2): this {
    this.x /= v.x;
    this.y /= v.y;

    return this;
  }

  public divideScalar(scalar: number): this {
    return this.multiplyScalar(1 / scalar);
  }

  public applyMatrix3(m: Matrix3): this {
    const x = this.x;
    const y = this.y;
    const e = m.elements;

    this.x = e[0] * x + e[3] * y + e[6];
    this.y = e[1] * x + e[4] * y + e[7];

    return this;
  }

  public min(v: Vector2): this {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);

    return this;
  }

  public max(v: Vector2): this {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);

    return this;
  }

  public clamp(min: Vector2, max: Vector2): this {
    // assumes min < max, componentwise

    this.x = Math.max(min.x, Math.min(max.x, this.x));
    this.y = Math.max(min.y, Math.min(max.y, this.y));

    return this;
  }

  public clampScalar(minVal: number, maxVal: number): this {
    min.set(minVal, minVal);
    max.set(maxVal, maxVal);

    return this.clamp(min, max);
  }

  public clampLength(min: number, max: number): this {
    const length = this.length();

    return this.divideScalar(length || 1).multiplyScalar(
      Math.max(min, Math.min(max, length)),
    );
  }

  public floor(): this {
    this.x = Math.floor(this.x);
    this.y = Math.floor(this.y);

    return this;
  }

  public ceil(): this {
    this.x = Math.ceil(this.x);
    this.y = Math.ceil(this.y);

    return this;
  }

  public round(): this {
    this.x = Math.round(this.x);
    this.y = Math.round(this.y);

    return this;
  }

  public roundToZero(): this {
    this.x = this.x < 0 ? Math.ceil(this.x) : Math.floor(this.x);
    this.y = this.y < 0 ? Math.ceil(this.y) : Math.floor(this.y);

    return this;
  }

  public negate(): this {
    this.x = -this.x;
    this.y = -this.y;

    return this;
  }

  public dot(v: Vector2): number {
    return this.x * v.x + this.y * v.y;
  }

  public cross(v: Vector2): number {
    return this.x * v.y - this.y * v.x;
  }

  public lengthSq(): number {
    return this.x * this.x + this.y * this.y;
  }

  public length(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  public manhattanLength(): number {
    return Math.abs(this.x) + Math.abs(this.y);
  }

  public normalize(): this {
    return this.divideScalar(this.length() || 1);
  }

  public angle(): number {
    // computes the angle in radians with respect to the positive x-axis

    let angle = Math.atan2(this.y, this.x);

    if (angle < 0) angle += 2 * Math.PI;

    return angle;
  }

  public distanceTo(v: Vector2): number {
    return Math.sqrt(this.distanceToSquared(v));
  }

  public distanceToSquared(v: Vector2): number {
    const dx = this.x - v.x;
    const dy = this.y - v.y;

    return dx * dx + dy * dy;
  }

  public manhattanDistanceTo(v: Vector2): number {
    return Math.abs(this.x - v.x) + Math.abs(this.y - v.y);
  }

  public setLength(length: number): this {
    return this.normalize().multiplyScalar(length);
  }

  public lerp(v: Vector2, alpha: number): this {
    this.x += (v.x - this.x) * alpha;
    this.y += (v.y - this.y) * alpha;

    return this;
  }

  public lerpVectors(v1: Vector2, v2: Vector2, alpha: number): this {
    return this.subVectors(v2, v1).multiplyScalar(alpha).add(v1);
  }

  public equals(v: Vector2): boolean {
    return v.x === this.x && v.y === this.y;
  }

  public fromArray(array: number[] | Float32Array, offset?: number): this {
    if (offset === undefined) offset = 0;

    this.x = array[offset];
    this.y = array[offset + 1];

    return this;
  }

  public toArray(
    array: number[] | Float32Array,
    offset?: number,
  ): number[] | Float32Array {
    if (array === undefined) array = [];
    if (offset === undefined) offset = 0;

    array[offset] = this.x;
    array[offset + 1] = this.y;

    return array;
  }

  // TODO add odie version!
  // fromBufferAttribute(attribute, index, offset)
  // {
  //     if (offset !== undefined)
  //     {
  //         console.warn('THREE.Vector2: offset has been removed from .fromBufferAttribute().');
  //     }

  //     this.x = attribute.getX(index);
  //     this.y = attribute.getY(index);

  //     return this;
  // }

  public rotateAround(center: Vector2, angle: number): this {
    const c = Math.cos(angle);
    const s = Math.sin(angle);

    const x = this.x - center.x;
    const y = this.y - center.y;

    this.x = x * c - y * s + center.x;
    this.y = x * s + y * c + center.y;

    return this;
  }

  public toString(): string {
    return `(${this.x}, ${this.y})`;
  }
}

const min = new Vector2();
const max = new Vector2();
